/**
 * Sound: small HTMLAudio pool over the Sonniss clips in /sfx.
 * Browser autoplay rules mean nothing plays until the first user gesture.
 */
const LEVELS: Record<string, number> = {
  shot: 0.35,
  explosion: 0.6,
  mine: 0.3,
  warp: 0.5,
  dock: 0.5,
  ui: 0.25,
  ambient: 0.14,
  shieldhit: 0.4,
};

export class Sound {
  private unlocked = false;
  private muted = localStorage.getItem('deepwake.muted') === '1';
  private ambient: HTMLAudioElement | null = null;
  private last = new Map<string, number>();

  constructor() {
    const unlock = () => {
      if (this.unlocked) return;
      this.unlocked = true;
      this.startAmbient();
    };
    addEventListener('pointerdown', unlock, { once: false });
    addEventListener('keydown', unlock, { once: false });

    const btn = document.createElement('button');
    btn.id = 'mutebtn';
    btn.textContent = this.muted ? '🔇' : '🔊';
    btn.addEventListener('click', () => {
      this.muted = !this.muted;
      localStorage.setItem('deepwake.muted', this.muted ? '1' : '0');
      btn.textContent = this.muted ? '🔇' : '🔊';
      if (this.ambient) this.ambient.volume = this.muted ? 0 : LEVELS.ambient;
    });
    document.body.appendChild(btn);

    // Soft UI clicks on every button
    addEventListener('click', (e) => {
      if ((e.target as HTMLElement)?.tagName === 'BUTTON') this.play('ui', 120);
    });
  }

  private startAmbient(): void {
    if (this.ambient) return;
    this.ambient = new Audio('/sfx/ambient.mp3');
    this.ambient.loop = true;
    this.ambient.volume = this.muted ? 0 : LEVELS.ambient;
    this.ambient.play().catch(() => {});
  }

  /** Play a clip, throttled per-name to avoid machine-gun stacking. */
  play(name: keyof typeof LEVELS | string, throttleMs = 90): void {
    if (!this.unlocked || this.muted) return;
    const now = performance.now();
    if (now - (this.last.get(name) ?? 0) < throttleMs) return;
    this.last.set(name, now);
    const a = new Audio(`/sfx/${name}.mp3`);
    a.volume = LEVELS[name] ?? 0.35;
    a.play().catch(() => {});
  }
}
